import * as Yup from 'yup';
import { Link } from '@mui/material';
import { websiteLink } from 'utils/misc';
import { STATE_OPTIONS } from './internal/constants';
import { NAME_VALIDATION, EMAIL_VALIDATION, PHONE_VALIDATION } from './internal/configValidations';
import configBasics from './internal/configBasics';
import type { JSX } from 'react';

type FieldType = 'text' | 'email' | 'pattern' | 'address' | 'radio' | 'checkbox' | 'textarea' | 'autocomplete';

export interface FormFieldConfig {
  label?: string | JSX.Element;
  title?: string;
  type: FieldType;
  pattern?: string;
  placeholder?: string;
  options?: { label: string; value: string }[];
  validation: Yup.AnySchema;
  conditionalValidation?: {
    message: string;
    testFn: (this: Yup.TestContext, value: string) => boolean;
  };
  defaultValue: number | string | string[];
  width?: number;
  rows?: number;
  required?: boolean;
  autoComplete?: string;
  suggestions?: readonly { id: string; fullName: string; abbreviation: string; country: string }[];
  hidden?: boolean;
}

type FieldConfig = { [key: string]: FormFieldConfig };

// config for all form fields (which may include fields not used in this instance)
export const fieldsConfig: FieldConfig = {
  first: {
    type: 'text',
    label: 'First name',
    validation: NAME_VALIDATION.required('Please enter first name.'),
    defaultValue: '',
    width: 6,
    required: true,
    autoComplete: 'given-name'
  },
  last: {
    label: 'Last name',
    type: 'text',
    validation: NAME_VALIDATION.required('Please enter last name.'),
    defaultValue: '',
    width: 6,
    required: true,
    autoComplete: 'family-name'
  },
  nametag: {
    label: 'Name for roster', // to handle aggressive autocomplete use 'First name for button' if only want first name
    type: 'text',
    validation: NAME_VALIDATION.required('Please enter name for roster.'),
    defaultValue: '',
    required: true,
    width: 12,
    autoComplete: configBasics.nametags.includeLastName ? 'name' : 'given-name'
  },
  email: {
    label: 'Email',
    type: 'email',
    validation: EMAIL_VALIDATION.required('Please enter email address.'),
    defaultValue: '',
    width: 6,
    required: true,
    autoComplete: 'email'
  },
  emailConfirmation: {
    label: 'Re-enter email',
    type: 'email',
    validation: EMAIL_VALIDATION.required('Please re-enter your email address.').test('email-match', 'Email addresses must match.', function (value) {
      return value?.toLowerCase() === this.parent.email?.toLowerCase();
    }),
    defaultValue: '',
    width: 6,
    required: true,
    autoComplete: 'email'
  },
  phone: {
    label: 'Phone',
    type: 'pattern',
    pattern: '###-###-####',
    placeholder: 'e.g. 555-555-5555',
    validation: PHONE_VALIDATION.required('Please enter phone number.'),
    defaultValue: '',
    width: 12,
    // width: 4,
    required: true,
    autoComplete: 'tel-national'
  },
  address: {
    label: 'Street address',
    type: 'address',
    validation: Yup.string(),
    defaultValue: '',
    width: 9,
    required: false,
    autoComplete: 'street-address'
  },
  apartment: {
    label: 'Apt, Suite, etc.',
    type: 'text',
    validation: Yup.string(),
    defaultValue: '',
    width: 3,
    autoComplete: 'address-line2'
  },
  city: {
    label: 'City',
    type: 'text',
    validation: Yup.string().required('Please enter city.'),
    defaultValue: '',
    width: 5,
    required: true,
    autoComplete: 'address-level2'
  },
  state: {
    label: 'State / Province',
    type: 'autocomplete',
    suggestions: STATE_OPTIONS,
    validation: Yup.string().required('Please enter state or province.'),
    defaultValue: '',
    width: 4,
    required: true,
    autoComplete: 'address-level1'
  },
  zip: {
    label: 'Zip code',
    type: 'text',
    validation: Yup.string(),
    defaultValue: '',
    width: 3,
    required: false,
    autoComplete: 'postal-code'
  },
  country: {
    label: 'Country',
    type: 'text',
    validation: Yup.string(),
    defaultValue: '',
    width: 12,
    autoComplete: 'country',
    hidden: true
  },
  age: {
    type: 'radio',
    title: "Age",
    label: "Please choose one.",
    options: [
      { label: 'Adult', value: 'adult' },
      { label: '13-17 yr old', value: '13-17' },
      { label: '6-12 yr old', value: '6-12' },
      { label: '3-5 yr old', value: '3-5' },
      { label: '0-2 yr old', value: '0-2' },
    ],
    required: true,
    validation: Yup.string().required('Please select age range.'),
    defaultValue: '',
  },
  share: {
    title: "Roster",
    type: 'checkbox',
    label: "What information do you want shared in the roster?",
    options: [
      { label: 'Include my name in the roster', value: 'name' },
      { label: 'Include my email in the roster', value: 'email' },
      { label: 'Include my phone number in the roster', value: 'phone' },
      { label: 'Include my city/state/zip in the roster', value: 'region' },
    ],
    validation: Yup.array(),
    defaultValue: ['name', 'email', 'phone', 'region'],
  },
  carpool: {
    type: 'checkbox',
    title: "Transportation",
    label: "Parking at Camp Sealth is limited. We encourage carpooling!",
    options: [
      { label: "I can offer a ride to camp", value: 'offer-ride' },
      { label: "I might be able to give a ride to camp", value: 'offer-ride-maybe' },
      { label: "I need a ride to camp", value: 'need-ride' },
      { label: "I might need a ride to camp", value: 'need-ride-maybe' },
    ],
    validation: Yup.array(),
    defaultValue: [],
  },
  volunteer: {
    type: 'checkbox',
    title: "Volunteering and work/trade",
    label: <>Everyone will be asked to help with camp, but we need a few people who can commit in advance or in larger ways.<p>We also have a limited number of work/trade positions available. If your financial situation is such that you would benefit from reduced registration fees in exchange for working a bit more at camp, this is a good choice for you. We have both half and full work/trades available, depending on need, interest, and availability. If you express interest, someone will contact you to discuss. If you are offered a position, a portion of your registration fees will be returned to you via check at the end of camp. We will generally offer work/trade opportunities in the order they are requested.</p></>,
    options: [
      { label: "I can come early to help with camp set up", value: 'setup' },
      { label: "I can stay late to help with camp take down", value: 'strike' },
      { label: "I am interested in a work/trade position that will involve a bigger volunteer job and some monetary reimbursement at the end of camp. I understand that I will be contacted to discuss details.", value: 'work-trade' },
    ],
    validation: Yup.array(),
    defaultValue: [],
  },
  allergies: {
    type: 'textarea',
    title: 'Allergy / Safety Information',
    label: "Please let us know about your safety and allergy needs. This is a great place to tell us about any severe food allergies! You may also include less severe allergies (food or not) and any other safety concerns you may have.",
    defaultValue: '',
    rows: 2,
    validation: Yup.string(),
  },
  housing: {
    type: 'checkbox',
    title: 'Camp housing',
    label: "Camp Sealth has a variety of cabins for us to sleep in. In order to best accommodate everyone, please let us know your preferences. You can select several boxes if multiple housing scenarios would work for you.",
    options: [
      { label: "All-gender dorm", value: 'all-gender' },
      { label: "Men's dorm", value: 'men' },
      { label: "Women's dorm", value: 'women' },
      { label: "Snorers' dorm", value: 'snorers' },
      { label: "Family dorm (all-gender, with children)", value: 'family' },
      { label: "I will bring my (short or medium length) RV", value: 'rv' },
      { label: "I will sleep off-site", value: 'off-site' },
    ],
    validation: Yup.array(),
    defaultValue: [],
  },
  housingNotes: {
    type: 'textarea',
    title: 'Additional housing needs',
    label: "Let us know any accessibility needs or requests you have around housing. This is a great place to let us know if you need housing with a restroom very close by, or if you have a hard time climbing stairs, or any other needs.",
    validation: Yup.string(),
    defaultValue: '',
    rows: 2
  },
  roommate: {
    type: 'textarea',
    title: 'Room sharing preferences',
    label: "We pre-assign housing and try our best to meet everyone's needs and preferences. If there are people you would like to room with, list their names here.",
    validation: Yup.string(),
    defaultValue: '',
    rows: 2
  },
  comments: {
    type: 'textarea',
    title: "Anything else?",
    label: "Tell us anything else you'd like us to know, including any skills you'd like to offer for this year's camp.  We want to be sure we don't miss anything that could make camp welcoming and enjoyable.",
    validation: Yup.string(),
    defaultValue: '',
    rows: 5,
  },
  agreement: {
    type: 'checkbox',
    title: 'Community Expectations',
    label: <>Do you agree that everyone you are registering will follow {configBasics.event.title}'s <Link href={websiteLink(configBasics.links.policies.safety)} color='secondary' target='_blank' rel='noreferrer'>community expectations</Link>?</>,
    required: true,
    options: [
      { label: 'Yes', value: 'yes' }
    ],
    validation: Yup.array(),
    conditionalValidation: {
      message: `You must agree to the community expectations.`,
      testFn: function (this: Yup.TestContext, value: string) {
        const personIndex = this.path.match(/people\[(\d+)\]/)?.[1];
        if (Number(personIndex) === 0) { // required for primary registrant only
          return Array.isArray(value) && value.includes('yes');
        } else {
          return true; // not required for additional members of party
        }
      }
    },
    defaultValue: [],
  },
  admission: {
    type: 'text',
    validation: Yup.number().min(configBasics.admissions.costRange[0]).max(configBasics.admissions.costRange[1]).required(),
    defaultValue: configBasics.admissions.costDefault,
  },
  deposit: {
    type: 'text',
    validation: Yup.number().min(0),
    defaultValue: 0,
  },
  fees: {
    type: 'text',
    validation: Yup.number().min(0),
    defaultValue: 0,
  }
} as const satisfies FieldConfig;
